import { Suspense } from 'react';

import clsx from 'clsx';

import DownloadButtons from '@/components/landing/download-buttons';
import MagicKeyboard from '@/components/landing/magic-keyboard';
import AiEditorMockup from '@/components/landing/mockups/ai-editor-mockup';
import CalendarMockup from '@/components/landing/mockups/calendar-mockup';
import InboxMockup from '@/components/landing/mockups/inbox-mockup';
import MailboxesMockup from '@/components/landing/mockups/mailboxes-mockup';
import ScaledMockup from '@/components/landing/mockups/scaled-mockup';
import SearchMockup from '@/components/landing/mockups/search-mockup';
import WindowFrame from '@/components/landing/mockups/window-frame';
import PrivateAiFlow from '@/components/landing/private-ai-flow';
import StorageGraphic from '@/components/landing/storage-graphic';

import SiteLayout from './site';

// -----------------------------------------------------------------------------
// Constants
// -----------------------------------------------------------------------------

const HEADLINE = 'Private AI for your work';

// Each feature shows a crop of its mockup: rendered at `width`, scaled by `scale`, pinned to the
// cell's top-left gutter.
const FEATURES = [
  {
    title: 'Search',
    description: 'A local index you can filter with from:, has: and is:.',
    mockup: <SearchMockup />,
    width: 600,
    scale: 0.62,
  },
  {
    title: 'Calendar',
    description:
      'Your Google calendars next to your mail. A date in an email opens that day beside it.',
    mockup: <CalendarMockup />,
    width: 900,
    scale: 0.5,
  },
  {
    title: 'AI editor',
    description: 'Ask AI to write or rewrite a draft, then review each change before it’s applied.',
    mockup: <AiEditorMockup />,
    width: 568,
    scale: 0.66,
  },
  {
    title: 'Important and Other',
    description: 'Promotions, social and newsletters go to Other, so Important is mostly people.',
    mockup: (
      <WindowFrame variant="bare" className="border-gray-6 rounded-xl border">
        <InboxMockup sidebar={false} rail={false} height={440} />
      </WindowFrame>
    ),
    width: 640,
    scale: 0.6,
  },
  {
    title: 'Multiple mailboxes',
    description: 'Add all your Google accounts and switch between them with ⌃1 to ⌃9.',
    mockup: <MailboxesMockup />,
    width: 300,
    scale: 1,
  },
];

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

const Landing: React.FC = () => (
  <SiteLayout>
    <section className="flex flex-col items-start gap-5 px-4 pt-12 pb-8 md:px-8 md:pt-24 md:pb-14">
      <h1 className="text-[2rem] leading-[1.1] font-normal tracking-[-0.03em] text-balance md:text-[2.75rem]">
        {HEADLINE}
      </h1>
      <p className="text-gray-11 max-w-[580px] text-lg leading-[1.6] text-pretty">
        Get through your email, calendar and the rest of your work faster. All your data is stored
        and processed privately.
      </p>
      <DownloadButtons />
    </section>
    <div
      inert
      aria-hidden
      className="overflow-hidden [mask-image:linear-gradient(to_bottom,#000_72%,transparent)] px-4 md:px-8"
    >
      <Suspense>
        <ScaledMockup width={1216} height={590}>
          <WindowFrame>
            <InboxMockup />
          </WindowFrame>
        </ScaledMockup>
      </Suspense>
    </div>
    <SellingPoint
      title="Private AI"
      description="Run AI fully on your device, free forever. For larger models, use Private Cloud: requests are encrypted end to end and only decrypted inside a verified enclave."
    >
      <PrivateAiFlow />
    </SellingPoint>
    <SellingPoint
      title="All your data is stored locally"
      description="Your mail, attachments and search index are stored on your device, in the app or in your browser. Everything works offline."
      reverse
    >
      <StorageGraphic />
    </SellingPoint>
    <section
      aria-label="Features"
      className="border-gray-5 bg-gray-5 grid grid-cols-1 gap-px border-t lg:grid-cols-3"
    >
      <Feature
        title="Keyboard-first"
        description="Every action has a shortcut. ⌘K finds the rest."
        art={
          <div className="p-4 md:p-8">
            <MagicKeyboard />
          </div>
        }
      />
      {FEATURES.map(({ title, description, mockup, width, scale }) => (
        <Feature
          key={title}
          title={title}
          description={description}
          art={
            <div
              className="absolute top-4 left-4 origin-top-left md:top-8 md:left-8"
              style={{ width, transform: `scale(${scale})` }}
            >
              {mockup}
            </div>
          }
        />
      ))}
    </section>
    <section className="border-gray-5 flex flex-col items-start gap-5 border-t px-4 py-14 md:px-8 md:py-24">
      <h2 className="text-[2rem] leading-[1.1] font-normal tracking-[-0.03em] md:text-4xl">
        {HEADLINE}
      </h2>
      <DownloadButtons />
    </section>
  </SiteLayout>
);

/** A selling point: text in one third of the frame, its graphic in the other two. */
const SellingPoint: React.FC<{
  title: string;
  description: string;
  reverse?: boolean;
  children: React.ReactNode;
}> = ({ title, description, reverse = false, children }) => (
  <section
    className={clsx(
      'border-gray-5 grid grid-cols-1 border-t',
      reverse
        ? 'md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]'
        : 'md:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]'
    )}
  >
    <div
      className={clsx(
        'flex flex-col gap-3 self-start px-4 pt-8 pb-4 md:sticky md:top-16 md:px-8 md:py-12',
        reverse && 'md:order-2'
      )}
    >
      <h2 className="text-2xl leading-[30px] font-medium tracking-[-0.02em]">{title}</h2>
      <p className="text-gray-11 text-[15px] leading-6">{description}</p>
    </div>
    <div
      className={clsx(
        'border-gray-5 bg-gray-2 min-w-0 p-4 md:px-8 md:py-12',
        reverse ? 'md:border-r' : 'md:border-l'
      )}
    >
      {children}
    </div>
  </section>
);

const Feature: React.FC<{
  title: string;
  description: string;
  art: React.ReactNode;
}> = ({ title, description, art }) => (
  <div className="bg-gray-1 flex min-w-0 flex-col">
    <div
      inert
      aria-hidden
      className="bg-gray-2 relative h-[272px] overflow-hidden [content-visibility:auto]"
    >
      <Suspense>{art}</Suspense>
    </div>
    <div className="flex flex-col gap-1.5 px-4 pt-5 pb-6 md:px-8 md:pb-8">
      <h3 className="text-[15px] leading-[22px] font-medium">{title}</h3>
      <p className="text-gray-11 text-sm leading-[22px]">{description}</p>
    </div>
  </div>
);

export default Landing;
